// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// React
import { useState, useEffect } from 'react';

// Sentry
import * as Sentry from "@sentry/react";



// ----------------------------------------------------------------------------
//   Tracked Error Objects
// ----------------------------------------------------------------------------


/**
 * Error state tokens / "codes".
 */
export const ErrorState = Object.freeze({

  /** User data could not be retrieved from the API. */
  NO_USER_DATA:      'NO_USER_DATA',

  /** Video data could not be retrieved from the API. */
  NO_VIDEO_DATA:     'NO_VIDEO_DATA',

  /** Device webcam could not be initialized. */
  NO_WEBCAM:         'NO_WEBCAM',

  /** List of media devices could not be loaded from the user's device. */
  NO_MEDIA_DEVICES:  'NO_MEDIA_DEVICES',

  /** The user's video recording failed to complete. */
  RECORDING_FAILED:  'RECORDING_FAILED',

  /** User recording could not be uploaded. */
  UPLOAD_FAILED:     'UPLOAD_FAILED',
  NO_PRACTICE_AUDIO: 'NO_PRACTICE_AUDIO',
  ACTIVITY_LOOKUP_FAILED: 'ACTIVITY_LOOKUP_FAILED',

  /** Local recordings could not be loaded. */
  GET_STORED_RECORDINGS_FAILED: 'GET_STORED_RECORDINGS_FAILED',

  /** Metadata on a stored recording could not be updated. */
  UPDATE_STORED_RECORDING_METADATA_FAILED: 'UPDATE_STORED_RECORDING_METADATA_FAILED',

  /** The reupload queue could not be fetched from the server. */
  REUPLOAD_QUEUE_FETCH_FAILED: 'REUPLOAD_QUEUE_FETCH_FAILED',
});


/**
 * Base class for errors that can provide structured context for Sentry.
 * Subclasses should override toSentryContext() to return a map of named
 * context blocks: { contextName: { key: value, ... }, ... }
 */
export class ContextualError extends Error {

  /**
   * Returns a map of named Sentry context blocks to attach when this error
   * is captured. Override in subclasses to provide structured context.
   * @returns {{ [contextName: string]: object }}
   */
  toSentryContext() {
    return {};
  }

  /**
   * Capture this error with Sentry, attaching all context from toSentryContext().
   */
  captureWithSentry() {
    Sentry.withScope((scope) => {
      Object.entries(this.toSentryContext()).forEach(([name, data]) => {
        scope.setContext(name, data);
      });
      Sentry.captureException(this);
    });
  }
}


/**
 * Custom error class indicating that the site is in a given error state.
 * By default, tracks this state (incl. the causing error) with Sentry.
 *
 * This error class focuses on application state (e.g. "can't use webcam")
 * as opposed to individual causes (e.g. "no devices found", "user won't
 * grant permissions", etc).
 * Multiple types of errors may be aggregated under one functional "state",
 * which directs what the application's fallback behavior will be.
 *
 * The cause is tracked on the linked `cause` error for debugging purposes.
 */
export class StateError extends Error {
  constructor( state, cause, options = { track: true } ) {

    // Fallback & unpacking for `options` object
    options = options ?? { track: true };
    const { track, ...superOptions } = options;

    // If causing error has a message, include it in this object's message
    const message = cause?.message ? `${state}: ${cause.message}` : state;

    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super( message, { ...superOptions, cause });

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, StateError);
    }

    // Fields
    this.name  = "StateError";
    this.state = state;
    this.track = track;

    // If desired, track this error with Sentry
    if (track && !(cause instanceof StateError && cause.track)) {
      if (cause instanceof ContextualError) {
        Sentry.withScope((scope) => {
          Object.entries(cause.toSentryContext()).forEach(([name, data]) => {
            scope.setContext(name, data);
          });
          Sentry.captureException( this, { tags: { state } } );
        });
      } else {
        Sentry.captureException( this, { tags: { state } } );
      }
    }
  }
}



// ----------------------------------------------------------------------------
//   Error Tracking
// ----------------------------------------------------------------------------


export function useTrackedError() {

  // Create new React state to track error
  const [ errorObject, setErrorObject ] = useState({ state: null, object: null, message: [] });


  // When error object is updated, track it with Sentry
  useEffect(() => {
    if ( errorObject?.state ) {
      trackError( errorObject.state, errorObject.object );
    }
  }, [ errorObject ]);


  // Set a new error state
  function set( errorState, errorObject, errorMessage = [] ) {
    setErrorObject({
      state:   errorState,
      object:  errorObject,
      message: errorMessage,
    })
  }


  // Clear the existing error state
  function clear() {
    setErrorObject({ state: null, object: null, message: [] });
  }


  // Return the React state object and the two modification functions
  return [ errorObject, { set, clear } ]
}


/**
 * Track an error state with Sentry.
 */
export function trackError(errorState, errorObject) {

  // Wrap the custom ErrorState and source error into a single JS Error object
  const wrappedError = new Error( errorState, {cause: errorObject} )

  // Track with Sentry
  Sentry.captureException(wrappedError);

  // Return the custom error object
  return wrappedError;
}



// ----------------------------------------------------------------------------
//   Specific Error Classes
// ----------------------------------------------------------------------------


/**
 * Custom error class indicating that the current user is not logged in.
 * Untracked -- should simply be handled client side with a login page redirect.
 */
export class NotLoggedInError extends Error {}


export class DBBackupError extends ContextualError {
  constructor( recording, reason = undefined, options = undefined ) {

    // Initialze parent object
    super(
      `Failed to retrieve backup recording.` + (reason ? ` Reason: ${reason}` : ''),
      options,
    )

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, DBBackupError);
    }

    // Fields
    this.name = "DBBackupError";
    this.recording = recording;
    this.reason    = reason;
  }

  toSentryContext() {
    return {
      'recordingID':    this.recording.recordingID,
      'recordingSize':  this.recording.blob?.size,
      'recordingTitle': this.recording.title,
      'errorReason':    this.reason,
    };
  }
}


export class UploadError extends ContextualError {
  constructor( cause, metadata, options ) {

    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super( `Failed to upload recording.`, { ...options, cause });

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UploadError);
    }

    // Fields
    this.name = "UploadError";
    this.metadata = { ...metadata, cause_message: cause?.message };
  }

  toSentryContext() {
    return {
      ...(this.metadata ?? {}),
      cause: (this.cause instanceof ContextualError ? this.cause?.toSentryContext() : {}),
    };
  }
}


export class BatchUploadError extends ContextualError {
  constructor( uploadResults, options = undefined ) {

    // Separate out uploads by status & save to this object, for tracking
    const successfulUploads = uploadResults.filter(({success}) =>  success);
    const failedUploads     = uploadResults.filter(({success}) => !success);

    // Initialze parent object
    super(
      `Failed ${ failedUploads.length }/${ uploadResults.length } uploads.`, options,
    )

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BatchUploadError);
    }

    // Fields
    this.name = "BatchUploadError";
    this.uploadResults     = uploadResults;
    this.successfulUploads = successfulUploads;
    this.failedUploads     = failedUploads;
    this.errors            = failedUploads.map(({error}) => error);  // mirrors AggregateError API
  }

  toSentryContext() {

    // Add context block for summary info
    const contexts = {
      'Upload Summary': {
        total:     this.successfulUploads.length + this.failedUploads.length,
        succeeded: this.successfulUploads.length,
        failed:    this.failedUploads.length,
      },
    };

    // Add one context block per upload
    this.uploadResults.forEach(({ success, recordingId, filename, error }, i) => {
      const label = success ? 'Succeeded' : 'Failed';
      contexts[`Upload ${i + 1} (${label})`] = {
        success,
        recordingId,
        filename,
        ...(error ? {
          error_message: error?.message ?? String(error),
          ...(error instanceof ContextualError ? error.toSentryContext() : {}),
        } : {}),
      };
    });

    return contexts;
  }

  /**
   * Override captureWithSentry to also capture each individual child error,
   * preserving the full .cause chain (and original stack trace) for each failure.
   */
  captureWithSentry() {
    Sentry.withScope((scope) => {
      Object.entries(this.toSentryContext()).forEach(([name, data]) => {
        scope.setContext(name, data);
      });

      // Capture each UploadError individually so Sentry follows .cause to the
      // original error (e.g. NotFoundError) and includes its stack trace.
      this.errors.forEach((err) => {
        Sentry.captureException(err);
      });

      Sentry.captureException(this);
    });
  }
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------


export default {
  ErrorState,
  ContextualError,
  StateError,
  useTrackedError,
  trackError,
  NotLoggedInError,
  DBBackupError,
  UploadError,
  BatchUploadError,
}
